import { Option } from "@inrev/core";
import type { z } from "zod";

type PrincipalCandidate = { name?: Option<string>; isPrimaryCompany?: Option<boolean> };
type PrincipalCandidates = Option<readonly Option<PrincipalCandidate>[]>;
type PrincipalArgs = { companies: PrincipalCandidates; principalName: Option<string> };

const namedCompanies = (companies: PrincipalCandidates) => {
	const candidates: readonly Option<PrincipalCandidate>[] = Option.getOrElse(companies, []);
	return candidates.filter(
		(company): company is PrincipalCandidate & { name: string } => !!company?.name,
	);
};

// Quick submit has no Principal field, and Full Submission only picks one when its Bond step
// opens. Without a choice, the principal is the company the Bond step would select by default.
// A choice is kept even when it no longer matches, so validation can ask the user to pick again.
export const resolvePrincipalName = ({
	companies,
	principalName,
}: PrincipalArgs): string | undefined => {
	if (principalName) return principalName;
	const named = namedCompanies(companies);
	return (named.find((company) => company.isPrimaryCompany) ?? named[0])?.name;
};

export const matchingPrincipalName = ({
	companies,
	principalName,
}: PrincipalArgs): string | undefined =>
	namedCompanies(companies).find((company) => company.name === principalName)?.name;

type PrincipalFormValues = {
	account?: Option<{ details?: Option<{ companies?: PrincipalCandidates }> }>;
	quote?: Option<{ principal?: Option<{ name?: Option<string> }> }>;
};

const refinePrincipalMatchesCompany = (values: PrincipalFormValues, ctx: z.RefinementCtx) => {
	const principalName = values.quote?.principal?.name;
	const companies = values.account?.details?.companies;
	if (!principalName || matchingPrincipalName({ companies, principalName })) return;
	ctx.addIssue({
		code: "custom",
		path: ["quote", "principal", "name"],
		message: `"${principalName}" is no longer one of this account's companies. Choose a principal.`,
	});
};

// Runs alongside other field errors, so the user sees it on the first submit.
export const withPrincipalMatchesCompany = <T extends z.ZodType<PrincipalFormValues>>(schema: T) =>
	schema.superRefine(refinePrincipalMatchesCompany, { when: () => true });
